import React from 'react';
import { 
    PendingThresholdChange,
    R2FProductionCapabilityData,
    R2FNodeData
} from 'src/features/types/r2fTypes';
import { R2FProductionCapabilityRow } from './R2FProductionCapabilityRow';
import { R2FNodeRow } from './R2FNodeRow';
import { AddR2FNode } from './AddR2FNode';

interface R2FTableRowsProps {
    productionCapabilities: R2FProductionCapabilityData[];
    expandedProductionCapabilities: Record<string, boolean>;
    pendingChanges: Record<string, Record<string, PendingThresholdChange>>;
    handleToggleExpand: (productionCapabilityId: string) => void;
    handleValueChange: (
        productionCapabilityId: string,
        handlingTier: string,
        low: number,
        high: number,
        action: string,
        reason?: string,
        actionSource?: string
    ) => void;
    handleAddNode: (productionCapabilityId: string, handlingTier: string) => void;
    handleDeleteNode: (productionCapabilityId: string, handlingTier: string) => void;
    onDelete: (productionCapabilityId: string, productionCapability: string, reason: string, actionSource?: string) => Promise<boolean>;
    onRemovePendingPC?: (pcName: string) => void;
    userCanEdit: boolean;
    warehouseId?: string;
}

/**
 * Component handling the rendering of table rows including PC rows, node rows, and add node UI
 */
export const R2FTableRows: React.FC<R2FTableRowsProps> = ({
    productionCapabilities,
    expandedProductionCapabilities,
    pendingChanges,
    handleToggleExpand,
    handleValueChange,
    handleAddNode,
    handleDeleteNode,
    onDelete,
    onRemovePendingPC,
    userCanEdit,
    warehouseId
}) => {
    const renderPCRows = () => {
        const allRows: JSX.Element[] = [];

        productionCapabilities.forEach((pc: R2FProductionCapabilityData) => {
            // Calculate if this production capability has any pending changes
            const pcPendingChanges = new Map<string, PendingThresholdChange>();
            const pcChanges = pendingChanges[pc.id];
            
            if (pcChanges) {
                Object.entries(pcChanges).forEach(([handlingTier, change]) => {
                    pcPendingChanges.set(handlingTier, change);
                });
            }
            
            const isExpanded = expandedProductionCapabilities[pc.id] || false;
            
            // Add the PC row
            allRows.push(
                <R2FProductionCapabilityRow
                    key={`pc-${pc.id}`}
                    productionCapability={pc}
                    isExpanded={isExpanded}
                    onToggleExpand={handleToggleExpand}
                    pendingChanges={pcPendingChanges}
                    onValueChange={handleValueChange}
                    onDelete={onDelete}
                    onRemovePendingPC={onRemovePendingPC}
                    userCanEdit={userCanEdit}
                    warehouseId={warehouseId}
                />
            );
            
            // Only add child rows if expanded
            if (isExpanded) {
                // Add the default node if it exists
                if (pc.defaultNode) {
                    const defaultNode = pc.defaultNode;
                    const pendingChange = pcPendingChanges.get(defaultNode.id);
                    
                    allRows.push(
                        <R2FNodeRow
                            key={`node-default-${defaultNode.id}`}
                            node={defaultNode}
                            productionCapabilityId={pc.id}
                            productionCapabilityName={pc.name}
                            pendingChange={pendingChange}
                            onValueChange={handleValueChange}
                            onDelete={handleDeleteNode}
                            userCanEdit={userCanEdit}
                            warehouseId={warehouseId}
                        />
                    );
                }
                
                // Add the additional nodes
                for (let i = 0; i < pc.additionalNodes.length; i++) {
                    const node = pc.additionalNodes[i];
                    // Include nodes that are pending deletion with appropriate styling
                    const pendingChange = pcPendingChanges.get(node.id);
                    
                    allRows.push(
                        <R2FNodeRow
                            key={`node-${node.id}`}
                            node={node}
                            productionCapabilityId={pc.id}
                            productionCapabilityName={pc.name}
                            pendingChange={pendingChange}
                            onValueChange={handleValueChange}
                            onDelete={handleDeleteNode}
                            userCanEdit={userCanEdit}
                            warehouseId={warehouseId}
                        />
                    );
                }
                
                // Add any pending "Add" nodes that don't exist yet in additionalNodes
                if (pcChanges) {
                    Object.entries(pcChanges).forEach(([handlingTier, change]) => {
                        if (change.action === 'Add') {
                            console.log(`[R2F-TABLE] Processing Add action for handling tier: ${handlingTier}, change:`, change);
                            
                            // We want to show ALL nodes including default ones as children
                            // No more skipping default nodes
                            const isSyntheticPCDefaultNode = false; // Always show all nodes
                            
                            // Debug logging
                            console.log(`[R2F-TABLE] Adding handling tier to UI: ${handlingTier}, handlingTier: ${change.handlingTier}`);
                            
                            // Create a temporary node object for the new node
                            const syntheticNode: R2FNodeData = {
                                id: handlingTier,
                                handlingTier: change.handlingTier,
                                lowThreshold: change.lowThreshold,
                                highThreshold: change.highThreshold,
                                isDefault: change.isDefault,
                                publishDateEpochMillis: Date.now(),
                                userAlias: 'Pending Addition',
                                isSynthetic: true,
                                isPendingAddition: true
                            };
                            
                            // Add to the rows array
                            allRows.push(
                                <R2FNodeRow
                                    key={`node-pending-${handlingTier}`}
                                    node={syntheticNode}
                                    productionCapabilityId={pc.id}
                                    productionCapabilityName={pc.name}
                                    pendingChange={change}
                                    onValueChange={handleValueChange}
                                    onDelete={handleDeleteNode}
                                    userCanEdit={userCanEdit}
                                    warehouseId={warehouseId}
                                />
                            );
                        }
                    });
                }
                
                // Remove the duplicate "add node" UI button since it's already in the parent row
                // This prevents having two "Add Handling Tier" buttons showing up
            }
        });
        
        return allRows;
    };
    
    if (productionCapabilities.length === 0) {
        return (
            <tr>
                <td colSpan={5} className="empty-table-message">
                    <span>No production capabilities available</span>
                </td>
            </tr>
        );
    }
    
    return <>{renderPCRows()}</>;
};
